// The SAN move list, as DOM beside the canvas: a side panel where there is
// room, otherwise a one-line strip under the board that expands on tap. In a
// replay each move is a button that jumps to its position.
import { clear, el } from '@bn-games/ui-shell';
import { t } from '../i18n/I18n.js';
import type { SanPly } from '../game/pgn.js';
import { moveCells, placeMoveList, type MoveListPlacement } from './moveListLayout.js';

export interface MoveListOptions {
  readonly canvas: HTMLCanvasElement;
  /** Replay: jump to the position after this many moves. A live game leaves it out. */
  readonly onSelect?: (plies: number) => void;
  /** Height taken at the bottom of the viewport by something else (the replay bar). */
  readonly bottomInset?: () => number;
  /** Shown under the list — in a strip, only while it is expanded. */
  readonly footer?: HTMLElement;
}

const DEFAULT_REM_PX = 16;

export class MoveListPanel {
  private readonly root: HTMLElement;
  private readonly list: HTMLElement;
  private readonly toggle: HTMLButtonElement;
  private readonly moves: HTMLElement[] = [];
  private signature = '';
  private placementKey = '';
  private expanded = false;
  private hidden = false;

  constructor(private readonly opts: MoveListOptions) {
    this.toggle = el('button', {
      class: 'chess-moves__toggle',
      attrs: { type: 'button' },
      on: { click: () => this.setExpanded(!this.expanded) },
    }) as HTMLButtonElement;
    this.list = el('div', { class: 'chess-moves__list', attrs: { role: 'group' } });
    const head = el('div', { class: 'chess-moves__head' }, [el('span', { class: 'chess-moves__title' }), this.toggle]);
    const foot = opts.footer === undefined ? null : el('div', { class: 'chess-moves__footer' }, [opts.footer]);
    this.root = el('aside', { class: 'chess-moves' }, [head, this.list, foot]);
    this.root.style.display = 'none';
    this.relabel();
    document.body.appendChild(this.root);
  }

  /** `shown` moves are on the board; the last of them is marked as current. */
  setMoves(plies: readonly SanPly[], shown: number = plies.length): void {
    const signature = plies.map((ply) => ply.san).join(' ');
    if (signature !== this.signature || plies.length !== this.moves.length) {
      this.signature = signature;
      this.rebuild(plies);
    }
    this.moves.forEach((node, ply) => {
      const current = ply === shown - 1;
      node.classList.toggle('chess-moves__move--current', current);
      if (current) node.setAttribute('aria-current', 'true');
      else node.removeAttribute('aria-current');
    });
    this.moves[shown - 1]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    if (shown === 0) this.list.scrollTo({ top: 0, left: 0 });
  }

  /** The player turned the list off (or back on); applied by the next `layout()`. */
  setHidden(hidden: boolean): void {
    this.hidden = hidden;
  }

  /** Follow the canvas. Cheap enough for every frame: it writes styles only when something moved. */
  layout(): void {
    const rect = this.opts.canvas.getBoundingClientRect();
    const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || DEFAULT_REM_PX;
    const placement: MoveListPlacement = this.hidden ? { mode: 'hidden' } : placeMoveList({
      canvas: rect,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      bottomInset: this.opts.bottomInset?.() ?? 0,
      remPx,
    });
    const key = JSON.stringify(placement);
    if (key === this.placementKey) return;
    this.placementKey = key;
    this.apply(placement);
  }

  /** Language changed: refresh the panel's own text. */
  relabel(): void {
    const title = this.root.querySelector('.chess-moves__title');
    if (title !== null) title.textContent = t('moveList.title');
    this.root.setAttribute('aria-label', t('moveList.title'));
    this.list.setAttribute('aria-label', t('moveList.title'));
    this.setExpanded(this.expanded);
    if (this.moves.length === 0) this.rebuild([]);
  }

  dispose(): void {
    this.root.remove();
  }

  private apply(placement: MoveListPlacement): void {
    const style = this.root.style;
    if (placement.mode === 'hidden') {
      style.display = 'none';
      return;
    }
    style.display = '';
    this.root.classList.toggle('chess-moves--side', placement.mode === 'side');
    this.root.classList.toggle('chess-moves--strip', placement.mode === 'strip');
    style.left = `${placement.left}px`;
    style.top = `${placement.top}px`;
    style.width = `${placement.width}px`;
    style.height = placement.mode === 'side' ? `${placement.height}px` : '';
    style.maxHeight = placement.mode === 'strip' ? `${placement.maxHeight}px` : '';
  }

  private setExpanded(expanded: boolean): void {
    this.expanded = expanded;
    this.root.classList.toggle('chess-moves--expanded', expanded);
    this.toggle.setAttribute('aria-expanded', String(expanded));
    this.toggle.textContent = expanded ? '▾' : '▴';
    const label = t(expanded ? 'moveList.collapse' : 'moveList.expand');
    this.toggle.setAttribute('aria-label', label);
    this.toggle.title = label;
  }

  private rebuild(plies: readonly SanPly[]): void {
    clear(this.list);
    this.moves.length = 0;
    if (plies.length === 0) {
      this.list.appendChild(el('span', { class: 'chess-moves__empty', text: t('moveList.empty') }));
      return;
    }
    const onSelect = this.opts.onSelect;
    for (const cell of moveCells(plies)) {
      if (cell.kind === 'number') this.list.appendChild(el('span', { class: 'chess-moves__num', text: cell.text }));
      else if (cell.kind === 'gap') this.list.appendChild(el('span', { class: 'chess-moves__gap' }));
      else {
        const node = onSelect === undefined
          ? el('span', { class: 'chess-moves__move', text: cell.san })
          : el('button', {
            class: 'chess-moves__move chess-moves__move--link',
            attrs: { type: 'button', 'aria-label': t('moveList.goTo', { move: cell.label }) },
            text: cell.san,
            on: { click: () => onSelect(cell.ply + 1) },
          });
        this.moves.push(node);
        this.list.appendChild(node);
      }
    }
  }
}
